import { Classification } from "./classifyError";
import { reportError } from "./reportError";

type UncaughtErrorSubscriber = (
  error: unknown,
  classification: Classification
) => void;

const subscribers = new Set<UncaughtErrorSubscriber>();

// React re-throws errors it has already surfaced (facebook/react#11499), so the
// same error object can arrive more than once; it is handled only the first time.
const handled = new WeakSet<WeakKey>();

const isDebuggerEvaluation = (error: unknown) =>
  error instanceof Error &&
  typeof error.stack === "string" &&
  error.stack.includes("debug-evaluate");

function handleUncaughtError(error: unknown): void {
  if (error && typeof error === "object") {
    if (handled.has(error)) {
      return;
    }
    handled.add(error);
  }

  if (isDebuggerEvaluation(error)) {
    return;
  }

  const classification = reportError(error);
  subscribers.forEach((subscriber) => subscriber(error, classification));
}

const onError = (event: ErrorEvent) =>
  handleUncaughtError(event.error ?? new Error(event.message));

const onUnhandledRejection = (event: PromiseRejectionEvent) =>
  handleUncaughtError(event.reason);

/**
 * The single capture point for uncaught errors and unhandled rejections; every
 * one is classified and reported here, exactly once. errorReporting.tsx installs
 * it once at startup and never removes it; the returned uninstall is for tests.
 */
export function installGlobalErrorListener(): () => void {
  window.addEventListener("error", onError);
  window.addEventListener("unhandledrejection", onUnhandledRejection);

  return () => {
    window.removeEventListener("error", onError);
    window.removeEventListener("unhandledrejection", onUnhandledRejection);
  };
}

/** Be told about each uncaught error after it has been classified and reported. */
export function subscribeToUncaughtErrors(
  subscriber: UncaughtErrorSubscriber
): () => void {
  subscribers.add(subscriber);
  return () => {
    subscribers.delete(subscriber);
  };
}
